import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineOutlined from "@mui/icons-material/HelpOutlineOutlined";

const FAQ_ITEMS = [
  {
    qKey: "aiClippingFaqWhat",
    q: "What is AI Clipping?",
    aKey: "aiClippingFaqWhatA",
    a: "AI Clipping turns long-form videos (podcasts, interviews, tutorials) into short vertical clips with captions. Paste a YouTube link or hosted file, pick a 1–20 minute window, and AI finds the best moments for TikTok, Reels, and Shorts.",
  },
  {
    qKey: "aiClippingFaqMoney",
    q: "How can I make money clipping videos?",
    aKey: "aiClippingFaqMoneyA",
    a: "Clip long videos into viral shorts, grow a theme page on TikTok or YouTube Shorts, and monetize through platform creator funds, brand deals, affiliate links in captions, or selling clipping services to podcasters and coaches. Consistency beats volume — post 1–3 clips daily from one long source.",
  },
  {
    qKey: "aiClippingFaqNiche",
    q: "What niches work best for clip monetization?",
    aKey: "aiClippingFaqNicheA",
    a: "Finance, self-improvement, comedy podcasts, gaming commentary, and business interviews perform well. Choose a niche you understand, clip the hook in the first 2 seconds, and add captions so clips work on mute.",
  },
  {
    qKey: "aiClippingFaqVideoType",
    q: "What videos work best?",
    aKey: "aiClippingFaqVideoTypeA",
    a: "Talking-head content with clear speech — podcasts, interviews, webinars, and vlogs. Avoid music-only or silent B-roll. Use a standard YouTube watch URL (not Shorts) and a segment with continuous dialogue.",
  },
  {
    qKey: "aiClippingFaqTime",
    q: "How long does processing take?",
    aKey: "aiClippingFaqTimeA",
    a: "Most jobs finish in 5–30 minutes depending on queue length and video length. Clips are saved to your library automatically — no rush to download before links expire.",
  },
  {
    qKey: "aiClippingFaqShare",
    q: "How do I post clips to social media?",
    aKey: "aiClippingFaqShareA",
    a: "Open My Clips, select a clip, and use Share to schedule a post on TikTok, Instagram, YouTube, or Facebook. Connect accounts under Scheduler first, or copy the caption and download the MP4.",
  },
];

export default function ClippingFaqAccordion({ lang }) {
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState(false);

  return (
    <Card
      variant="outlined"
      data-tour="ai-clipping-faq"
      sx={{
        borderRadius: 2,
        position: { md: "sticky" },
        top: { md: 24 },
        bgcolor: alpha(theme.palette.primary.main, 0.02),
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
          <HelpOutlineOutlined color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={800}>
            {lang?.aiClippingFaqTitle || "Frequently asked questions"}
          </Typography>
        </Stack>

        <Stack spacing={0.5}>
          {FAQ_ITEMS.map((item, idx) => (
            <Accordion
              key={item.qKey}
              disableGutters
              elevation={0}
              expanded={expanded === idx}
              onChange={(_, isOpen) => setExpanded(isOpen ? idx : false)}
              sx={{
                bgcolor: "transparent",
                "&:before": { display: "none" },
                borderRadius: "8px !important",
                border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48 }}>
                <Typography variant="body2" fontWeight={700}>
                  {lang?.[item.qKey] || item.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                  {lang?.[item.aKey] || item.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
